import { useToast } from '@chakra-ui/react';
import { showToast } from '@diamond/shared/ui';
import { adminCaptureException, apiInstance } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { ENDPOINT_PRODUCT_HIGHLIGHT } from '../constant';

const ProductHighlightItemQueryKey = 'ProductHighlightItems';

export const useAddHighlightItems = () => {
  const token = useAuthStore((s) => s.access_token);
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    Error,
    { highlightId: string; products: string[] }
  >({
    mutationFn: async ({ highlightId, products }) => {
      const { data } = await apiInstance(token).post(
        `${ENDPOINT_PRODUCT_HIGHLIGHT}/${highlightId}/item`,
        { products }
      );
      return data;
    },
    onSuccess: (_, { highlightId }) => {
      // Invalidate the items query to refetch
      queryClient.invalidateQueries({
        queryKey: [ProductHighlightItemQueryKey, highlightId],
      });
    },
    onError: (error, { highlightId, products }) => {
      const respData = (
        axios.isAxiosError(error) ? error.response?.data ?? {} : {}
      ) as { statusCode?: number; message?: string };

      adminCaptureException(
        {
          message:
            respData.message ??
            (error instanceof Error ? error.message : 'Unknown error'),
          statusCode:
            respData.statusCode ??
            (axios.isAxiosError(error) ? error.response?.status ?? 0 : 0),
        },
        (scope) => {
          scope.setTags({
            feature: 'highlight',
            action: 'addProductItem',
            status_code: respData.statusCode,
          });
          scope.setContext('error', {
            error: respData,
          });
          scope.setExtra('addProductItem', {
            highlightId,
            products,
          });
          scope.setLevel('error');
        }
      );

      showToast(
        toast,
        'error',
        `Gagal mengupdate item,\n message: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    },
  });
};

export const useDeleteHighlightItems = () => {
  const token = useAuthStore((s) => s.access_token);
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    Error,
    { highlightId: string; products: string[] }
  >({
    mutationFn: async ({ highlightId, products }) => {
      const { data } = await apiInstance(token).delete(
        `${ENDPOINT_PRODUCT_HIGHLIGHT}/${highlightId}/item`,
        { data: { products } }
      );
      return data;
    },
    onSuccess: (_, { highlightId }) => {
      // Invalidate the items query to refetch
      queryClient.invalidateQueries({
        queryKey: [ProductHighlightItemQueryKey, highlightId],
      });
    },
    onError: (error, { highlightId, products }) => {
      const respData = (
        axios.isAxiosError(error) ? error.response?.data ?? {} : {}
      ) as { statusCode?: number; message?: string };

      adminCaptureException(
        {
          message:
            respData.message ??
            (error instanceof Error ? error.message : 'Unknown error'),
          statusCode:
            respData.statusCode ??
            (axios.isAxiosError(error) ? error.response?.status ?? 0 : 0),
        },
        (scope) => {
          scope.setTags({
            feature: 'highlight',
            action: 'deleteProductItem',
            status_code: respData.statusCode,
          });
          scope.setContext('error', {
            error: respData,
          });
          scope.setExtra('deleteProductItem', {
            highlightId,
            products,
          });
          scope.setLevel('error');
        }
      );

      showToast(
        toast,
        'error',
        `Gagal menghapus item,\n message: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    },
  });
};

export const useReorderHighlightItem = () => {
  const token = useAuthStore((s) => s.access_token);
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    Error,
    { highlightId: string; productId: string; index: number }
  >({
    mutationFn: async ({ highlightId, productId, index }) => {
      const { data } = await apiInstance(token).patch(
        `${ENDPOINT_PRODUCT_HIGHLIGHT}/${highlightId}/item/${productId}/reorder`,
        { index }
      );
      return data;
    },
    retry: 0,
    onSuccess: (_, { highlightId }) => {
      // Invalidate the items query to refetch
      queryClient.invalidateQueries({
        queryKey: [ProductHighlightItemQueryKey, highlightId],
      });
    },
    onSettled: (_, __, { highlightId }) => {
      queryClient.invalidateQueries({
        queryKey: [ProductHighlightItemQueryKey, highlightId],
      });
    },
    onError: (error, { highlightId, productId, index }) => {
      const respData = (
        axios.isAxiosError(error) ? error.response?.data ?? {} : {}
      ) as { statusCode?: number; message?: string };

      adminCaptureException(
        {
          message:
            respData.message ??
            (error instanceof Error ? error.message : 'Unknown error'),
          statusCode:
            respData.statusCode ??
            (axios.isAxiosError(error) ? error.response?.status ?? 0 : 0),
        },
        (scope) => {
          scope.setTags({
            feature: 'highlight',
            action: 'reorderHighlightItem',
            status_code: respData.statusCode,
          });
          scope.setContext('error', {
            error: respData,
          });
          scope.setExtra('reorderHighlightItem', {
            highlightId,
            productId,
            index,
          });
          scope.setLevel('error');
        }
      );

      showToast(
        toast,
        'error',
        `Gagal mengubah urutan item,\n message: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    },
  });
};
